import React, { useState, useEffect } from 'react';
import { Heart, ExternalLink, Users, Target, Music, Sparkles, ArrowRight, Share2, Twitter, Facebook, Link2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import { MusicHeart, SequinGlitter } from '@/components/GoFundMe';

const GOFUNDME_URL = 'https://www.gofundme.com/f/help-open-the-brownstone-jazz-club';

// Hook to fetch campaign data
const useCampaignData = () => {
  const [data, setData] = useState({
    raised: 636,
    goal: 16500,
    donors: 6,
    lastUpdated: null
  });
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/gofundme/stats`);
        if (response.data) {
          setData({
            raised: response.data.raised_amount,
            goal: response.data.goal_amount,
            donors: response.data.donor_count,
            lastUpdated: response.data.last_updated
          });
        }
      } catch (error) {
        console.log('Using default campaign data');
      }
    };
    
    fetchData();
  }, []);
  
  return data;
};

// Share buttons component
const ShareButtons = () => {
  const shareText = `Help open The Brownstone Jazz Club in Uptown Minneapolis! 🎷🎵`;
  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(GOFUNDME_URL)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(GOFUNDME_URL)}`,
  };
  
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(GOFUNDME_URL);
      toast.success('Link copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };
  
  return (
    <div className="flex items-center justify-center gap-3">
      <span className="text-white/50 text-sm">Share:</span>
      <a 
        href={shareLinks.twitter}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 rounded-full bg-[#1DA1F2]/20 hover:bg-[#1DA1F2]/30 text-[#1DA1F2] transition-colors"
        title="Share on Twitter"
      >
        <Twitter className="w-5 h-5" />
      </a>
      <a 
        href={shareLinks.facebook}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 rounded-full bg-[#4267B2]/20 hover:bg-[#4267B2]/30 text-[#4267B2] transition-colors"
        title="Share on Facebook"
      >
        <Facebook className="w-5 h-5" />
      </a>
      <button 
        onClick={copyToClipboard}
        className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-colors"
        title="Copy link"
      >
        <Link2 className="w-5 h-5" />
      </button>
    </div>
  );
};

const SupportUs = () => {
  const campaign = useCampaignData();
  const progressPercent = Math.min((campaign.raised / campaign.goal) * 100, 100);

  return (
    <div className="min-h-screen bg-obsidian pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Sequin glitter effect - many sparkles */}
        <SequinGlitter count={50} />
        
        {/* Shimmer overlay */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-rose-500/5 to-transparent -translate-x-full animate-shimmer-slow" />
        </div>
        
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-rose-600/20 via-pink-600/10 to-obsidian" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(244,63,94,0.15),transparent_50%)]" />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-rose-500/20 border border-rose-500/30 rounded-full px-4 py-2 mb-6">
              <MusicHeart className="w-4 h-4 text-rose-400" clefSize="sm" animated />
              <span className="text-rose-300 text-sm font-medium">Community Fundraiser</span>
            </div>
            
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
              Help Open<br />
              <span className="text-rose-400">The Brownstone Jazz Club</span>
            </h1>
            
            <p className="text-white/70 text-lg sm:text-xl max-w-2xl mx-auto mb-8">
              Join us in bringing live jazz and vibrant community spirit back to Uptown Minneapolis. 
              Your support makes the dream possible.
            </p>

            {/* Progress Card */}
            <div className="bg-obsidian-light/80 backdrop-blur border border-white/10 rounded-2xl p-6 sm:p-8 max-w-lg mx-auto mb-8 relative overflow-hidden">
              {/* Card shimmer */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full animate-shimmer-slow" />
              </div>
              
              <div className="flex items-baseline justify-between mb-3 relative">
                <div>
                  <span className="text-4xl font-display text-white">${campaign.raised.toLocaleString()}</span>
                  <span className="text-white/50 ml-2">raised</span>
                </div>
                <span className="text-white/50">${campaign.goal.toLocaleString()} goal</span>
              </div>
              
              {/* Progress bar */}
              <div className="h-4 bg-white/10 rounded-full overflow-hidden mb-3 relative">
                <div 
                  className="h-full bg-gradient-to-r from-rose-500 via-pink-500 to-rose-400 rounded-full relative"
                  style={{ width: `${progressPercent}%` }}
                >
                  <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.3),transparent)] animate-shimmer" />
                </div>
              </div>
              
              <div className="flex items-center justify-between text-sm mb-6 relative">
                <span className="text-rose-400 font-medium">{progressPercent.toFixed(0)}% of goal</span>
                <span className="text-white/50 flex items-center gap-1">
                  <Users className="w-4 h-4" /> {campaign.donors} donors
                </span>
              </div>

              <a 
                href={GOFUNDME_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white py-6 text-lg font-medium gap-2 transition-all hover:scale-[1.02] justify-center">
                  <MusicHeart className="w-4 h-4" clefSize="sm" />
                  Donate Now
                  <ExternalLink className="w-4 h-4 ml-1" />
                </Button>
              </a>
              
              {/* Share buttons */}
              <div className="mt-6 pt-6 border-t border-white/10">
                <ShareButtons />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 bg-obsidian-light">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl text-white mb-6">Our Story</h2>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 text-lg leading-relaxed mb-6">
              The Brownstone Jazz Club is on a mission to bring live jazz and vibrant community spirit 
              back to Uptown Minneapolis. After a sharp decline in 2020, the Uptown community is slowly 
              revitalizing, and the Brownstone Jazz Club wants to be a part of this exciting transformation.
            </p>
            
            <p className="text-white/70 text-lg leading-relaxed mb-6">
              However, to open our doors, we urgently need capital. There are significant expenses from 
              city and state permit and licensing requirements, which are essential for us to operate 
              legally and safely.
            </p>

            <p className="text-white/70 text-lg leading-relaxed">
              Your financial support will help to fast track the opening of The Brownstone Jazz Club. 
              With your help, we can create a space where music, culture, and community thrive together. 
              Thank you for believing in our vision and supporting the next chapter of Uptown's story.
            </p>
          </div>
        </div>
      </section>

      {/* What Your Support Provides */}
      <section className="py-16 bg-obsidian">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl text-white mb-8 text-center">What Your Support Provides</h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-obsidian-light border border-white/10 rounded-xl p-6 text-center hover:border-rose-500/30 transition-colors">
              <div className="w-14 h-14 rounded-full bg-rose-500/20 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📜</span>
              </div>
              <h3 className="text-white font-medium mb-2">Permits & Licensing</h3>
              <p className="text-white/50 text-sm">City and state requirements to operate legally</p>
            </div>
            
            <div className="bg-obsidian-light border border-white/10 rounded-xl p-6 text-center hover:border-rose-500/30 transition-colors">
              <div className="w-14 h-14 rounded-full bg-rose-500/20 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎷</span>
              </div>
              <h3 className="text-white font-medium mb-2">Sound Equipment</h3>
              <p className="text-white/50 text-sm">Quality audio for live performances</p>
            </div>
            
            <div className="bg-obsidian-light border border-white/10 rounded-xl p-6 text-center hover:border-rose-500/30 transition-colors">
              <div className="w-14 h-14 rounded-full bg-rose-500/20 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">✨</span>
              </div>
              <h3 className="text-white font-medium mb-2">Venue Finishing</h3>
              <p className="text-white/50 text-sm">Final touches for a welcoming space</p>
            </div>
            
            <div className="bg-obsidian-light border border-white/10 rounded-xl p-6 text-center hover:border-rose-500/30 transition-colors">
              <div className="w-14 h-14 rounded-full bg-rose-500/20 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎉</span>
              </div>
              <h3 className="text-white font-medium mb-2">Grand Opening</h3>
              <p className="text-white/50 text-sm">A vibrant launch celebration</p>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-16 bg-obsidian-light">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl text-white mb-8 text-center">The Impact</h2>
          
          <div className="grid sm:grid-cols-2 gap-8">
            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-electric/20 flex items-center justify-center flex-shrink-0">
                <Music className="w-6 h-6 text-electric" />
              </div>
              <div>
                <h3 className="text-white font-display text-lg mb-2">Live Music Revival</h3>
                <p className="text-white/60">Bringing weekly jazz performances back to Uptown, creating a cultural destination for music lovers.</p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-electric/20 flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6 text-electric" />
              </div>
              <div>
                <h3 className="text-white font-display text-lg mb-2">Artist Development</h3>
                <p className="text-white/60">A platform for local artists at all stages of their careers to perform and grow.</p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-electric/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-electric" />
              </div>
              <div>
                <h3 className="text-white font-display text-lg mb-2">Community Space</h3>
                <p className="text-white/60">A welcoming venue where everyone feels at home, fostering connection and culture.</p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-electric/20 flex items-center justify-center flex-shrink-0">
                <Target className="w-6 h-6 text-electric" />
              </div>
              <div>
                <h3 className="text-white font-display text-lg mb-2">Neighborhood Revival</h3>
                <p className="text-white/60">Contributing to the revitalization of Uptown Minneapolis as a vibrant destination.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-rose-600/20 via-pink-600/10 to-obsidian relative overflow-hidden">
        {/* Sequin glitter effect */}
        <SequinGlitter count={40} />
        
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <div className="relative inline-block mb-6">
            <MusicHeart className="w-12 h-12 text-rose-400" clefSize="lg" />
            <div className="absolute inset-0 bg-rose-400/20 rounded-full animate-ping-slow" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-white mb-4">
            Every Dollar Counts
          </h2>
          <p className="text-white/70 text-lg mb-8">
            Whether it's $5 or $500, your contribution brings us closer to opening our doors 
            and sharing the joy of live jazz with our community.
          </p>
          
          <a 
            href={GOFUNDME_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white px-10 py-6 text-lg font-medium gap-2 transition-all hover:scale-[1.02] justify-center">
              <MusicHeart className="w-4 h-4" clefSize="sm" />
              Support Us on GoFundMe
              <ArrowRight className="w-5 h-5 ml-1" />
            </Button>
          </a>
          
          {/* Share buttons */}
          <div className="mt-8">
            <ShareButtons />
          </div>
          
          <p className="text-white/40 text-sm mt-6">
            Organized by Dena Allen • Minneapolis, MN
          </p>
        </div>
      </section>
    </div>
  );
};

export default SupportUs;
